/**
 * Column layout for the v8 DataTable.
 *
 * Runs on the TanStack Table **v8** engine. The runtime machinery: measure the scroll container,
 * resolve runtime widths through {@link resolveColumnWidths}, and sync them to the shared
 * `--utw-*` CSS variables on the container DOM node — so geometry flows through CSS variables,
 * NOT React state. A drag-resize therefore causes ZERO React re-renders per frame and the
 * memoized `<tbody>` (`DataTable`'s `TableBody`) never re-renders during a resize: the body
 * reads no width prop — every cell's inline `width: var(--utw-w-<id>)` picks the value up from
 * the container, so the body subtree never re-renders for geometry.
 *
 * Every DataTable gets a resolved layout. `columnConfigId` only keys the persistence of the
 * user's width ratios; without it the columns resolve from their declared weights each mount.
 *
 * v8 sizing is absolute-px only, so we DELIBERATELY do NOT use v8's built-in
 * `columnResizeMode` pixel resizing — our weighted flex-reflow (AG-Grid semantics: other flex
 * columns re-flow around the dragged one) plus ratio-of-container persistence stays APP logic.
 * v8's `column.getSize()` is never read on a header or cell.
 *
 * Input shape: this hook takes the v8 visible leaf columns and derives the runtime meta itself
 * (reading {@link DataTableColumnMeta} off each `column.columnDef.meta`).
 */
import type { Column } from '@tanstack/react-table';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resolveColumnWidths } from '../list-table/columnLayout/resolveColumnWidths';
import type { ManagedRuntimeColumn, ResolvedColumnWidths } from '../list-table/columnLayout/types';
import { useColumnLayoutPersistence } from '../list-table/columnLayout/useColumnLayoutPersistence';
import { columnWidthVar, stickyLeftVar, TABLE_MIN_WIDTH_VAR } from './cssVars';
import type { DataTableColumnMeta } from './types';

const EMPTY_RESOLVED: ResolvedColumnWidths = { widths: {}, tableMinPx: 0, stickyLefts: {} };
const EMPTY_OVERRIDES: Record<string, number> = {};

/** Read a column's layout meta off its v8 def. Defaulted so a column declaring none gets the
 *  weight/min defaults from {@link toRuntimeColumn}. */
const readMeta = (meta: unknown): DataTableColumnMeta => (meta ?? {}) as DataTableColumnMeta;

/**
 * Write the resolved geometry to the shared `--utw-*` CSS variables on the container element.
 * Uses the same var-name helpers from the shared `cssVars`, so both engines emit identical
 * variable names/values for an identical layout.
 */
const writeVars = (element: HTMLElement, resolved: ResolvedColumnWidths): void => {
    element.style.setProperty(TABLE_MIN_WIDTH_VAR, `${resolved.tableMinPx}px`);
    for (const [id, px] of Object.entries(resolved.widths)) {
        element.style.setProperty(columnWidthVar(id), `${px}px`);
    }
    for (const [id, left] of Object.entries(resolved.stickyLefts)) {
        element.style.setProperty(stickyLeftVar(id), `${left}px`);
    }
};

type DragState = {
    colId: string;
    startX: number;
    startPx: number;
    minPx: number;
    maxPx?: number;
    livePx: number;
    moved: boolean;
};

type UseDataTableLayoutProps<DataType> = {
    /** Persistence key for the user's width ratios (localStorage 'list-columns', or the key a
     *  surrounding `ColumnLayoutStorageContext` names). Absent ⇒ the columns resolve from their
     *  declared weights on every mount and a resize is not kept. */
    columnConfigId: string | undefined;
    /** v8 visible leaf columns, in render order — the layout follows visibility + order changes. */
    visibleLeafColumns: Column<DataType, unknown>[];
    /** Per-column extra minimum width (px) the layout must honour on top of the column's own
     *  `minPx`. Used so the totals row (whose formatted value can be wider than the weight-derived
     *  width) is never clipped — DataTable measures each totals cell's intrinsic content width and
     *  feeds it here, so showing/hiding totals reflows the columns to fit. A `fixedPx` column is
     *  never widened (its size is intentional); others are capped at their `maxPx`. */
    extraMinPx?: Record<string, number>;
};

/**
 * Per-column runtime input to `resolveColumnWidths`, derived from a column's managed meta and the
 * defaults a column that declares none falls back to (`widthWeight` 1, `minPx` 80; a `fixedPx`
 * column is frozen at its px both as min and max so it's excluded from flex distribution).
 */
const toRuntimeColumn = <DataType>(column: Column<DataType, unknown>): ManagedRuntimeColumn => {
    const meta = readMeta(column.columnDef.meta);
    return {
        id: column.id,
        widthWeight: meta.widthWeight ?? 1,
        minPx: meta.fixedPx ?? meta.minPx ?? 80,
        maxPx: meta.fixedPx ?? meta.maxPx,
        fixedPx: meta.fixedPx,
        sticky: !!meta.sticky,
        absorbSlack: meta.absorbSlack,
    };
};

/**
 * Column-layout orchestration for the v8 DataTable. Returns the container callback ref (the
 * node the `--utw-*` vars are written on and the ResizeObserver watches), the resolved sticky
 * prefix, and the resize handlers consumed by {@link DataTableColumnResizeHandle}: the pointer-driven
 * ones, the keyboard's step and the width each handle reports.
 */
export const useDataTableLayout = <DataType>({
    columnConfigId,
    visibleLeafColumns,
    extraMinPx,
}: UseDataTableLayoutProps<DataType>) => {
    const persistence = useColumnLayoutPersistence(columnConfigId);
    const { saveWidthRatio, removeWidthRatio } = persistence;
    const overrides = persistence.entry?.widthRatios ?? EMPTY_OVERRIDES;

    // Derive the runtime geometry meta for the currently VISIBLE columns (hidden columns free
    // their space automatically because they're absent from this list).
    const managedColumns = useMemo<ManagedRuntimeColumn[]>(
        () => visibleLeafColumns.map(toRuntimeColumn),
        [visibleLeafColumns]
    );

    // Fold the per-column totals floor into each column's `minPx` so width resolution + live resize
    // never produce a width narrower than the totals content. `fixedPx` columns keep their size; the
    // floor is capped at `maxPx`. Sticky-prefix derivation below stays on `managedColumns` (sticky
    // does not depend on minPx), so it doesn't recompute when only the totals floor changes.
    const effectiveColumns = useMemo<ManagedRuntimeColumn[]>(() => {
        if (!extraMinPx) return managedColumns;
        return managedColumns.map((column) => {
            const extra = extraMinPx[column.id];
            if (!extra || column.fixedPx !== undefined) return column;
            const capped = column.maxPx !== undefined ? Math.min(extra, column.maxPx) : extra;
            return capped > column.minPx ? { ...column, minPx: capped } : column;
        });
    }, [managedColumns, extraMinPx]);

    const [containerWidth, setContainerWidth] = useState(0);

    const containerElRef = useRef<HTMLElement | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const scrollCleanupRef = useRef<(() => void) | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const metaRef = useRef(managedColumns);
    const widthRef = useRef(containerWidth);
    const overridesRef = useRef(overrides);
    const dragRef = useRef<DragState | null>(null);
    const rafRef = useRef(0);

    const resolved = useMemo(
        () => (containerWidth > 0 ? resolveColumnWidths(effectiveColumns, containerWidth, overrides) : EMPTY_RESOLVED),
        [effectiveColumns, containerWidth, overrides]
    );
    const resolvedRef = useRef(resolved);

    useLayoutEffect(() => {
        metaRef.current = effectiveColumns;
        widthRef.current = containerWidth;
        overridesRef.current = overrides;
        resolvedRef.current = resolved;
    });

    // Sync resolved geometry to the CSS variables before paint. While a drag is in flight its
    // rAF loop owns the variables (an unrelated re-render must not snap the column back); the
    // commit on pointer-up triggers a final pass through here.
    useLayoutEffect(() => {
        const element = containerElRef.current;
        if (!element || dragRef.current) return;
        writeVars(element, resolved);
    }, [resolved]);

    /** Sticky columns must form a prefix of the visible order; the resolved prefix is what
     *  actually renders sticky. */
    const stickyIds = useMemo(() => {
        const ids = new Set<string>();
        for (const column of managedColumns) {
            if (!column.sticky) break;
            ids.add(column.id);
        }
        return ids;
    }, [managedColumns]);
    const lastStickyId = useMemo(() => {
        let last: string | undefined;
        for (const column of managedColumns) {
            if (!column.sticky) break;
            last = column.id;
        }
        return last;
    }, [managedColumns]);

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        const declared = managedColumns.filter((column) => column.sticky).length;
        if (declared > stickyIds.size) {
            console.warn(
                `[DataTable${columnConfigId ? ` columnConfigId="${columnConfigId}"` : ''}]: sticky columns must form a prefix of the visible column order; non-prefix sticky columns are rendered non-sticky.`
            );
        }
    }, [managedColumns, stickyIds, columnConfigId]);

    const containerRef = useCallback((node: HTMLElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;
        scrollCleanupRef.current?.();
        scrollCleanupRef.current = null;
        clearTimeout(debounceRef.current);
        containerElRef.current = node;
        if (!node) return;

        // Synchronous first measure: a commit-phase setState re-renders before paint, so the
        // first visible frame already has resolved widths (a deferred setTimeout would paint
        // one mis-layouted frame — do not defer the first measure).
        setContainerWidth(Math.round(node.clientWidth));

        const observer = new ResizeObserver(() => {
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                const element = containerElRef.current;
                if (!element) return;
                // Rounded so a vertical scrollbar flapping the width by sub-pixels can't loop.
                setContainerWidth(Math.round(element.clientWidth));
            }, 75);
        });
        observer.observe(node);
        observerRef.current = observer;

        // data-scrolled drives the "content hidden behind the sticky columns" edge shadow. Read in the
        // capture phase, ahead of the virtualizer's own handler that renders the rows a scroll brings
        // in: read after that render, the offset would force a layout on every scrolled frame.
        const onScroll = () => {
            const element = containerElRef.current;
            if (element) element.toggleAttribute('data-scrolled', element.scrollLeft > 0);
        };
        node.addEventListener('scroll', onScroll, { passive: true, capture: true });
        onScroll();
        scrollCleanupRef.current = () => node.removeEventListener('scroll', onScroll, { capture: true });
    }, []);

    const onResizeStart = useCallback((event: React.PointerEvent<HTMLElement>, colId: string) => {
        const meta = metaRef.current.find((column) => column.id === colId);
        if (!containerElRef.current || !meta) return;
        // Keep the header sort from firing and text from selecting.
        event.preventDefault();
        event.stopPropagation();
        const handle = event.currentTarget;
        try {
            handle.setPointerCapture(event.pointerId);
        } catch {
            // Synthetic pointer events (tests/automation) have no active pointer to capture;
            // without capture the drag still works while the pointer stays over the handle.
        }
        handle.setAttribute('data-resizing', 'true');
        const startPx = resolvedRef.current.widths[colId] ?? meta.minPx;
        dragRef.current = {
            colId,
            startX: event.clientX,
            startPx,
            minPx: meta.minPx,
            maxPx: meta.maxPx,
            livePx: startPx,
            moved: false,
        };
    }, []);

    const onResizeMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        if (dx !== 0) drag.moved = true;
        const nextPx = Math.round(
            Math.min(Math.max(drag.startPx + dx, drag.minPx), drag.maxPx ?? Number.POSITIVE_INFINITY)
        );
        if (nextPx === drag.livePx) return;
        drag.livePx = nextPx;
        if (rafRef.current) return;
        // One CSS-variable write batch per frame, no React re-render during the drag — this is
        // exactly why the memoized body does not re-render: geometry only touches the container's
        // CSS vars (each cell's inline `width: var(--utw-w-<id>)` reads them), never any React
        // state or body prop.
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = 0;
            const liveDrag = dragRef.current;
            const element = containerElRef.current;
            const containerPx = widthRef.current;
            if (!liveDrag || !element || containerPx <= 0) return;
            const live = resolveColumnWidths(metaRef.current, containerPx, {
                ...overridesRef.current,
                [liveDrag.colId]: liveDrag.livePx / containerPx,
            });
            writeVars(element, live);
        });
    }, []);

    const onResizeEnd = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            const drag = dragRef.current;
            if (!drag) return;
            dragRef.current = null;
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = 0;
            }
            const handle = event.currentTarget;
            handle.removeAttribute('data-resizing');
            if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
            const containerPx = widthRef.current;
            if (drag.moved && containerPx > 0) {
                // Single persistence write per drag (ratio of container, so it scales across
                // resolutions); the resulting re-render re-resolves to the exact values the drag
                // already painted.
                saveWidthRatio(drag.colId, drag.livePx / containerPx);
            } else if (containerElRef.current) {
                writeVars(containerElRef.current, resolvedRef.current);
            }
        },
        [saveWidthRatio]
    );

    /** Double-click on the handle: drop the override, back to weight-based width. */
    const onResizeReset = useCallback(
        (colId: string) => {
            removeWidthRatio(colId);
        },
        [removeWidthRatio]
    );

    /** An arrow key on the handle: the width moves by `delta` px within the column's limits, and is kept as a
     *  drag's is. */
    const onResizeBy = useCallback(
        (colId: string, delta: number) => {
            const meta = metaRef.current.find((column) => column.id === colId);
            const containerPx = widthRef.current;
            if (!meta || containerPx <= 0) return;
            const current = resolvedRef.current.widths[colId] ?? meta.minPx;
            const next = Math.round(
                Math.min(Math.max(current + delta, meta.minPx), meta.maxPx ?? Number.POSITIVE_INFINITY)
            );
            if (next !== current) saveWidthRatio(colId, next / containerPx);
        },
        [saveWidthRatio]
    );

    /** What each handle reports as its value: the column's width now and its limits, in px. A column with no
     *  `maxPx` has no real upper limit, so it reports the table's width, or its own once it is wider. It follows
     *  the committed widths: a drag updates it as it ends. */
    const resizeValues = useMemo(() => {
        const values: Record<string, { now: number; min: number; max: number }> = {};
        for (const column of effectiveColumns) {
            const now = Math.round(resolved.widths[column.id] ?? column.minPx);
            values[column.id] = { now, min: column.minPx, max: column.maxPx ?? Math.max(containerWidth, now) };
        }
        return values;
    }, [effectiveColumns, resolved, containerWidth]);

    return {
        containerRef,
        stickyIds,
        lastStickyId,
        onResizeStart,
        onResizeMove,
        onResizeEnd,
        onResizeReset,
        onResizeBy,
        resizeValues,
    };
};
